import { SARAmount, formatSAR } from "../../utils/currency";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getDataAPIAuth, postDataAPI } from "../../utils/API";
import GlobalTypes from "../../redux/actions/GlobalTypes";
import { dialog } from "../Common/TreasuryDialog";
import FormattedNumber from "../Common/FormattedNumber";
import { useTranslation } from "react-i18next";

// Mobile breakpoint hook
const useIsMobile = (bp = 768) => {
  const [isMobile, setIsMobile] = React.useState(() => window.innerWidth <= bp);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= bp);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [bp]);
  return isMobile;
};

const GOLD = "#c9a84c";
const DARK = "#0e3725";

const ViewProperty = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const isMobile = useIsMobile();
	const [data, setData] = useState([]);
	const [blockchain, setBlockchain] = useState([]);
	const [buysell, setTransaction] = useState([]);
	const [propid, setPropId] = useState("");
	const [availabletoken, setAvailableToken] = useState("");
	const [availablebalance, setAvailableBalance] = useState("");
	const [changedata, setChangeData] = useState({ units: "", amount: "" });
	const [tokenPrice, setTokenPrice] = useState(0);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [buyOpen, setBuyOpen] = useState(false);
	const [sellOpen, setSellOpen] = useState(false);
	const [activeImg, setActiveImg] = useState(0);
	const [feeSettings, setFeeSettings] = useState({ subscriptionFee: 0, exchangeFeePerParty: 0.25 });
	const [userTokenBalance, setUserTokenBalance] = useState(0);
	const [sellPrice, setSellPrice] = useState("");
	const [lastTradedPrice, setLastTradedPrice] = useState(null);
	const [sellUnits, setSellUnits] = useState("");
	const [askPrice, setAskPrice] = useState(null);
	const [bidPrice, setBidPrice] = useState(null);
	const [myOpenOrders, setMyOpenOrders] = useState([]);
	const [confirmSub, setConfirmSub] = useState(null); // pending subscription details for confirmation modal

	const { auth } = useSelector((state) => state);
	// formatSAR imported from utils/currency
	const pct = (a, b) => b > 0 ? Math.round((a / b) * 100) : 0;

	function changeData(e) {
		const { value, max } = e.target;
		if (!value) { setChangeData({ units: "", amount: "" }); return; }
		if (parseInt(value) > parseInt(max)) { dialog.error(t("dialog.errorTitle"), t("dialog.insufficientBalanceMsg")); return; }
		const amount = parseInt(value) * tokenPrice;
		setChangeData({ units: value, amount: amount });
	}

	async function getProperty() {
		try {
			setLoading(true);
			setError(null);
			var hrefPath = window.location.href;
			var id = hrefPath.split("/")[5];
			if (!id) { setError("Invalid property ID"); setLoading(false); return; }
			const token = auth?.data?.accesstoken || auth?.data?.access_token;
			var getPropertyRes = await getDataAPIAuth("/get_property/" + id, token);
			var getBlockchainRes = await getDataAPIAuth("/getPropBlockchainData/" + id, token);
			var getPropTransactionRes = await getDataAPIAuth("/getPropTransaction/" + id + "?isSubscription=true", token);
			setData(Array.isArray(getPropertyRes.data) ? getPropertyRes.data : []);
			setBlockchain(Array.isArray(getBlockchainRes.data) ? getBlockchainRes.data : []);
			setTransaction(Array.isArray(getPropTransactionRes.data) ? getPropTransactionRes.data : []);
		} catch (err) {
			console.error("Error loading property:", err);
			setError("Failed to load property data. Please try again.");
		} finally {
			setLoading(false);
		}
	}

	useEffect(() => {
		getProperty();
		// Fetch live fee settings from backend
		const token = auth?.data?.accesstoken || auth?.data?.access_token;
		if (token) {
			getDataAPIAuth("/fee-settings", token)
				.then(res => { if (res.data?.settings) setFeeSettings(res.data.settings); })
				.catch(() => {});
		}
	}, []);

	// Step 1: validate and show confirmation modal (buy/subscription) or submit directly (sell)
	const transaction = async (e) => {
		e.preventDefault();
		const { units, amount, propertyId, action } = e.target.elements;
		if (!units.value || parseInt(units.value) === 0) {
			dialog.error(t("dialog.errorTitle"), t("dialog.pleaseEnterUnits")); return;
		}
		if (parseInt(units.value) > parseInt(units.max)) { dialog.error(t("dialog.errorTitle"), t("dialog.notEnoughUnits")); return; }
		if (amount && parseFloat(amount.value) > parseFloat(availablebalance)) { dialog.error(t("dialog.errorTitle"), t("dialog.insufficientBalanceMsg")); return; }
		const token = auth?.data?.accesstoken || auth?.data?.access_token;
		if (!token) { dialog.error("Error", "Please log in again."); return; }
		const sellPriceEl = e.target.elements['sellPrice'];
		let details = {
			units: units.value,
			amount: amount ? amount.value : 0,
			propertyId: propertyId.value,
			userId: (auth.data) ? auth.data.user._id : "",
			action: action.value,
			price: sellPriceEl ? parseFloat(sellPriceEl.value) || 0 : 0,
		};
		// For buy (subscription): show confirmation modal first
		if (details.action === 'buy') {
			const qty = parseInt(units.value);
			const subtotal = qty * tokenPrice;
			const feeRate = (feeSettings.subscriptionFee || 0) / 100;
			const vatRate = (feeSettings.vatRate || 15) / 100;
			const feeBase = Math.round(subtotal * feeRate * 100) / 100;
			const feeVat = Math.round(feeBase * vatRate * 100) / 100;
			const feeTotal = Math.round((feeBase + feeVat) * 100) / 100;
			const grandTotal = Math.round((subtotal + feeTotal) * 100) / 100;
			setConfirmSub({ details, qty, unitPrice: tokenPrice, subtotal, feeRate: feeSettings.subscriptionFee || 0, feeBase, feeVat, feeTotal, grandTotal });
			return;
		}
		// For sell: submit directly
		await executeTransaction(details);
	};

	// Step 2: actually submit after confirmation
	const executeTransaction = async (details) => {
		const token = auth?.data?.accesstoken || auth?.data?.access_token;
		var url = details.action === "buy" ? "buy" : "sell";
		try {
			const response = await postDataAPI(url, details, token);
			if (response.data.status === 0) {
				const errMsg = response.data.errors
					? Object.values(response.data.errors).join(" ")
					: "Transaction failed. Please try again.";
				dialog.error("Error", errMsg);
			} else {
				setBuyOpen(false);
				setSellOpen(false);
				// Update local balance display without touching auth state
				if (response.data.newBalance !== undefined) {
					setAvailableBalance(response.data.newBalance);
				}
				const rawMsg = response.data.msg || response.data.message || "";
				const successMsg = details.action === 'sell'
					? (rawMsg || "Your sell order has been placed on the exchange. It will fill when a matching buyer is found.")
					: (rawMsg || "Your subscription was successful!");
				const successTitle = details.action === 'sell'
					? (rawMsg.includes("transaction") ? "Order Filled" : "Sell Order Placed")
					: "Subscription Confirmed";
				dialog.success(successTitle, successMsg);
				getProperty();
			}
		} catch (err) {
			const msg = err?.response?.data?.msg || err.message || "An error occurred. Please try again.";
			dialog.error("Error", msg);
		}
	};

	const Buy = async (property) => {
		setPropId(property._id);
		setAvailableToken(property.totalTokenSupply - property.tokenSupply);
		setAvailableBalance((auth.data) ? auth.data.user.totalBalance : "");
		setTokenPrice(property.tokenPrice || 0);
		setChangeData({ units: "", amount: "" });
		setBuyOpen(true);
	};

	const Sell = async (property) => {
		setPropId(property._id);
		setTokenPrice(property.tokenPrice || 0);
		setSellPrice(String(property.tokenPrice || ""));
		setSellUnits("");
		// Fetch user's actual token balance for this property
		try {
			const token = auth?.data?.accesstoken || auth?.data?.access_token;
			const userId = auth?.data?.user?._id;
			if (userId && token) {
				const balRes = await getDataAPIAuth(`/getUserBalance/${userId}`, token);
				const balances = Array.isArray(balRes.data) ? balRes.data : [];
				const propBalance = balances.find(b => {
					const pid = b.propertyId?._id || b.propertyId;
					return String(pid) === String(property._id);
				});
				setUserTokenBalance(propBalance ? propBalance.units : 0);
				setAvailableToken(propBalance ? propBalance.units : 0);
			} else {
				setUserTokenBalance(0);
				setAvailableToken(0);
			}
			// Fetch last traded price
			const txRes = await getDataAPIAuth(`/getPropTransaction/${property._id}`, auth?.data?.accesstoken || auth?.data?.access_token);
			const txs = Array.isArray(txRes.data) ? txRes.data : [];
			const lastTx = txs.find(t => t.price && t.price > 0);
			setLastTradedPrice(lastTx ? lastTx.price : property.tokenPrice);
			// Fetch exchange order book for Ask/Bid prices and user's open orders
			try {
				const sellOrdersRes = await getDataAPIAuth(`/getPropTrade/${property._id}?action=sell`, auth?.data?.accesstoken || auth?.data?.access_token);
				const buyOrdersRes = await getDataAPIAuth(`/getPropTrade/${property._id}?action=buy`, auth?.data?.accesstoken || auth?.data?.access_token);
				const sellOrders = Array.isArray(sellOrdersRes.data) ? sellOrdersRes.data : [];
				const buyOrders = Array.isArray(buyOrdersRes.data) ? buyOrdersRes.data : [];
				// Best ask = lowest open sell price; best bid = highest open buy price
				const sortedAsks = [...sellOrders].sort((a, b) => a.price - b.price);
				const sortedBids = [...buyOrders].sort((a, b) => b.price - a.price);
				setAskPrice(sortedAsks.length > 0 ? sortedAsks[0].price : null);
				setBidPrice(sortedBids.length > 0 ? sortedBids[0].price : null);
				// Pre-fill sell price with best ask or token price
				const refPrice = sortedAsks.length > 0 ? sortedAsks[0].price : property.tokenPrice;
				setSellPrice(String(refPrice || ""));
				// User's outstanding sell orders
				const userId = auth?.data?.user?._id;
				const myOrders = sellOrders.filter(o => String(o.userId) === String(userId));
				setMyOpenOrders(myOrders);
			} catch (e) {
				setAskPrice(null);
				setBidPrice(null);
				setMyOpenOrders([]);
			}
		} catch (e) {
			setUserTokenBalance(0);
			setAvailableToken(0);
		}
		setSellOpen(true);
	};

	const handleClose = () => { setBuyOpen(false); setSellOpen(false); };

	if (loading) return (
		<div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh" }}>
			<div style={{ textAlign: "center" }}>
				<div style={{ width: 40, height: 40, border: `3px solid ${GOLD}`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 12px" }} />
				<p style={{ color: "#6b7280", fontSize: 14 }}>Loading property…</p>
			</div>
			<style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
		</div>
	);

	if (error) return (
		<div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh" }}>
			<div style={{ textAlign: "center", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 12, padding: "32px 40px" }}>
				<p style={{ color: "#dc2626", fontSize: 15, margin: 0 }}>{error}</p>
			</div>
		</div>
	);

	const property = data[0];
	if (!property) return (
		<div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh" }}>
			<p style={{ color: "#6b7280" }}>Property not found.</p>
		</div>
	);

	const bc = blockchain[0];
	const images = Array.isArray(property.imageName) && property.imageName.length > 0 ? property.imageName : ["/img/al_narjes.jpg"];
	const subscribed = property.tokenSupply || 0;
	const total = property.totalTokenSupply || 1;
	const progress = pct(subscribed, total);

	return (
		<div style={{ padding: "28px 24px", maxWidth: 1100, margin: "0 auto" }}>
			<style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

			{/* Breadcrumb */}
			<div style={{ fontSize: 13, color: "#9ca3af", marginBottom: 20 }}>
				<a href="/dashboard" style={{ color: "#9ca3af", textDecoration: "none" }}>Dashboard</a>
				<span style={{ margin: "0 8px" }}>›</span>
				<span style={{ color: "#374151", fontWeight: 600 }}>{property.title}</span>
			</div>

				<div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 380px", gap: 24, alignItems: "start" }}>
				{/* Left Column */}
				<div>
					{/* Image Gallery */}
					<div style={{ background: "#111", borderRadius: 16, overflow: "hidden", marginBottom: 20, position: "relative", aspectRatio: "16/9" }}>
						<img src={images[activeImg]} alt={property.title}
							style={{ width: "100%", height: "100%", objectFit: "cover" }}
							onError={e => { e.target.src = "/img/al_narjes.jpg"; }} />
						{images.length > 1 && (
							<>
								<button onClick={() => setActiveImg(i => (i - 1 + images.length) % images.length)}
									style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", background: "rgba(0,0,0,0.5)", border: "none", borderRadius: "50%", width: 36, height: 36, color: "#fff", cursor: "pointer", fontSize: 18 }}>‹</button>
								<button onClick={() => setActiveImg(i => (i + 1) % images.length)}
									style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "rgba(0,0,0,0.5)", border: "none", borderRadius: "50%", width: 36, height: 36, color: "#fff", cursor: "pointer", fontSize: 18 }}>›</button>
								<div style={{ position: "absolute", bottom: 12, left: "50%", transform: "translateX(-50%)", display: "flex", gap: 6 }}>
									{images.map((_, i) => (
										<button key={i} onClick={() => setActiveImg(i)}
											style={{ width: 8, height: 8, borderRadius: "50%", border: "none", background: i === activeImg ? GOLD : "rgba(255,255,255,0.5)", cursor: "pointer", padding: 0 }} />
									))}
								</div>
							</>
						)}
					</div>

					{/* Property Info */}
					<div style={{ background: "#fff", borderRadius: 16, padding: "24px 28px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", marginBottom: 20 }}>
						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
							<div>
								<h1 style={{ fontSize: 22, fontWeight: 800, color: "#111827", margin: "0 0 6px" }}>{property.title}</h1>
								<p style={{ fontSize: 13.5, color: "#6b7280", margin: 0 }}>📍 {property.address}</p>
							</div>
							<span style={{ background: property.status === 1 ? "#d1fae5" : "#f3f4f6", color: property.status === 1 ? "#059669" : "#6b7280", padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
								{property.status === 1 ? "Active" : "Inactive"}
							</span>
						</div>
						<p style={{ fontSize: 14, color: "#374151", lineHeight: 1.7, margin: "0 0 20px" }}>{property.description}</p>
						<div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(3, 1fr)", gap: 12 }}>
							{[
								{ label: "Property Value", value: formatSAR(property.propertyEstimatedValue) },
								{ label: "Offering %", value: `${property.percentageOfOwnership || 0}%` },
								{ label: "Total Tokens", value: (property.totalTokenSupply || 0).toLocaleString() },
							].map((s, i) => (
								<div key={i} style={{ background: "#f9fafb", borderRadius: 10, padding: "14px 16px" }}>
									<p style={{ fontSize: 11.5, color: "#9ca3af", margin: "0 0 4px", fontWeight: 600, textTransform: "uppercase" }}>{s.label}</p>
									<p style={{ fontSize: 16, fontWeight: 700, color: "#111827", margin: 0 }}>{s.value}</p>
								</div>
							))}
						</div>
					</div>

					{/* Blockchain Info */}
					{bc && (
						<div style={{ background: "#fff", borderRadius: 16, padding: "20px 24px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", marginBottom: 20 }}>
							<h3 style={{ fontSize: 14, fontWeight: 700, color: "#374151", margin: "0 0 14px", display: "flex", alignItems: "center", gap: 8 }}>
								<span style={{ width: 8, height: 8, borderRadius: "50%", background: "#10b981", display: "inline-block" }} />
								On-Chain Data
							</h3>
							<div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
								{[
									{ label: "Contract Address", value: bc.contractAddress ? `${bc.contractAddress.slice(0, 18)}…` : "—", link: bc.contractAddress ? `https://testnet.bscscan.com/address/${bc.contractAddress}` : null },
									{ label: "Transaction Hash", value: bc.transactionHash ? `${bc.transactionHash.slice(0, 18)}…` : "—", link: bc.transactionHash ? `https://testnet.bscscan.com/tx/${bc.transactionHash}` : null },
									{ label: "Token Symbol", value: bc.tokenSymbol || bc.symbol || "—" },
									{ label: "Network", value: bc.network || "BSC TestNet" },
								].map((item, i) => (
									<div key={i} style={{ background: "#f9fafb", borderRadius: 8, padding: "10px 14px" }}>
										<p style={{ fontSize: 11, color: "#9ca3af", margin: "0 0 3px", fontWeight: 600, textTransform: "uppercase" }}>{item.label}</p>
										{item.link ? (
											<a href={item.link} target="_blank" rel="noreferrer" style={{ fontSize: 12.5, color: GOLD, fontWeight: 600, textDecoration: "none", wordBreak: "break-all" }}>{item.value}</a>
										) : (
											<p style={{ fontSize: 12.5, fontWeight: 600, color: "#374151", margin: 0 }}>{item.value}</p>
										)}
									</div>
								))}
							</div>
						</div>
					)}

					{/* Transactions */}
					<div style={{ background: "#fff", borderRadius: 16, padding: "20px 24px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
						<h3 style={{ fontSize: 14, fontWeight: 700, color: "#374151", margin: "0 0 16px" }}>Recent Transactions</h3>
						{buysell.length > 0 ? (
							<div style={{ overflowX: "auto" }}>
								<table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
									<thead>
										<tr style={{ borderBottom: "2px solid #f3f4f6" }}>
											{["Units", "Total Price", "Type", "Date"].map(h => (
												<th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: 11.5, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase" }}>{h}</th>
											))}
										</tr>
									</thead>
									<tbody>
										{buysell.map((b, i) => (
											<tr key={i} style={{ borderBottom: "1px solid #f9fafb" }}>
												<td style={{ padding: "10px 12px", fontWeight: 600 }}>{b.units}</td>
												<td style={{ padding: "10px 12px" }}>{formatSAR(b.units * property.tokenPrice)}</td>
												<td style={{ padding: "10px 12px" }}>
													<span style={{ background: b.action === "buy" ? "#d1fae5" : "#fef3c7", color: b.action === "buy" ? "#059669" : "#d97706", padding: "2px 10px", borderRadius: 12, fontSize: 11.5, fontWeight: 600, textTransform: "capitalize" }}>{b.action}</span>
												</td>
												<td style={{ padding: "10px 12px", color: "#6b7280" }}>{b.createdAt ? b.createdAt.split("T")[0] : "—"}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						) : (
							<div style={{ textAlign: "center", padding: "32px", color: "#9ca3af" }}>
								<p style={{ fontSize: 13, margin: 0 }}>No transactions yet</p>
							</div>
						)}
					</div>
				</div>

				{/* Right Column — Subscription Card */}
				<div style={{ position: "sticky", top: 24 }}>
					<div style={{ background: "#fff", borderRadius: 16, padding: "24px", boxShadow: "0 4px 24px rgba(0,0,0,0.1)", border: `1px solid ${GOLD}30` }}>
						<div style={{ marginBottom: 20 }}>
							<p style={{ fontSize: 12, color: "#9ca3af", margin: "0 0 4px", fontWeight: 600, textTransform: "uppercase" }}>Unit Price</p>
							<p style={{ fontSize: 28, fontWeight: 800, color: DARK, margin: 0 }}>
							<FormattedNumber value={property.tokenPrice} prefix="﷼ " decimals={2} decimalSize="0.65em" />
						</p>
						</div>
						<div style={{ marginBottom: 20 }}>
							<div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, color: "#6b7280", marginBottom: 6 }}>
								<span>Subscribed</span>
								<span style={{ fontWeight: 700, color: DARK }}>{progress}%</span>
							</div>
							<div style={{ background: "#f3f4f6", borderRadius: 6, height: 8, overflow: "hidden" }}>
								<div style={{ width: `${progress}%`, height: "100%", background: `linear-gradient(90deg, ${DARK}, ${GOLD})`, borderRadius: 6 }} />
							</div>
							<div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, color: "#9ca3af", marginTop: 4 }}>
								<span>{subscribed.toLocaleString()} units sold</span>
								<span>{(total - subscribed).toLocaleString()} remaining</span>
							</div>
						</div>
						<div style={{ background: "#f9fafb", borderRadius: 10, padding: "14px 16px", marginBottom: 20 }}>
							{[
								{ label: "Total Supply", value: total.toLocaleString() + " tokens" },
								{ label: "Your Balance", value: formatSAR(auth.data?.user?.totalBalance || 0) },
							].map((s, i) => (
								<div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: i === 0 ? 8 : 0 }}>
									<span style={{ color: "#6b7280" }}>{s.label}</span>
									<span style={{ fontWeight: 700, color: "#111827" }}>{s.value}</span>
								</div>
							))}
						</div>
					{property.status === 1 ? (
						<div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
							{/* Subscription phase: show Subscribe button only */}
							{subscribed < total ? (
								<>
									<button onClick={() => Buy(property)}
										style={{ width: "100%", padding: "14px", background: `linear-gradient(135deg, ${DARK}, #1a5c3a)`, color: GOLD, border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
										Subscribe Now
									</button>
									<div style={{ background: "#fffbeb", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#92400e", border: "1px solid #fde68a", textAlign: "center" }}>
										⏳ Subscription Open — Secondary market available after full subscription
									</div>
								</>
							) : (
								<>
									<div style={{ background: "#f0fdf4", borderRadius: 8, padding: "10px 14px", fontSize: 12.5, color: "#15803d", border: "1px solid #bbf7d0", textAlign: "center", fontWeight: 600 }}>
										✓ Subscription Complete — Secondary Market Open
									</div>
									<button onClick={() => Sell(property)}
										style={{ width: "100%", padding: "12px", background: "transparent", color: "#6b7280", border: "1.5px solid #e5e7eb", borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
										Sell Units
									</button>
								</>
							)}
						</div>
					) : (
						<div style={{ background: "#f9fafb", borderRadius: 10, padding: "16px", textAlign: "center", color: "#9ca3af", fontSize: 13 }}>
							This property is not currently open for subscription
						</div>
					)}
						<div style={{ marginTop: 16, padding: "12px 14px", background: "#fffbeb", borderRadius: 8, fontSize: 11.5, color: "#92400e", border: "1px solid #fde68a" }}>
							<strong>Fee Notice:</strong> Subscription: {feeSettings.subscriptionFee}% · Exchange: {feeSettings.exchangeFeePerParty}% per party
						</div>
					</div>
				</div>
			</div>

			{/* Subscription Confirmation Modal */}
			{confirmSub && (
				<div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100 }}>
					<div style={{ background: "#fff", borderRadius: 16, padding: "32px", width: "100%", maxWidth: 440, boxShadow: "0 24px 64px rgba(0,0,0,0.22)" }}>
						{/* Header */}
						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
							<div>
								<h3 style={{ fontSize: 18, fontWeight: 700, color: DARK, margin: 0 }}>Confirm Subscription</h3>
								<p style={{ fontSize: 13, color: "#6b7280", margin: "4px 0 0" }}>Please review your order before confirming</p>
							</div>
							<button onClick={() => setConfirmSub(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: 22, lineHeight: 1 }}>×</button>
						</div>
						{/* Order Summary */}
						<div style={{ background: "#f9fafb", borderRadius: 10, padding: "16px 18px", marginBottom: 18 }}>
							<div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, marginBottom: 10 }}>
								<span style={{ color: "#6b7280" }}>Quantity</span>
								<span style={{ fontWeight: 700, color: DARK }}>{confirmSub.qty.toLocaleString()} units</span>
							</div>
							<div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, marginBottom: 10 }}>
								<span style={{ color: "#6b7280" }}>Unit Price</span>
								<span style={{ fontWeight: 700, color: DARK }}>{formatSAR(confirmSub.unitPrice)}</span>
							</div>
							<div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, paddingTop: 10, borderTop: "1px solid #e5e7eb", marginBottom: 10 }}>
								<span style={{ color: "#6b7280" }}>Subtotal</span>
								<span style={{ fontWeight: 600, color: DARK }}>{formatSAR(confirmSub.subtotal)}</span>
							</div>
							{confirmSub.feeRate > 0 ? (
								<>
									<div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 6 }}>
										<span style={{ color: "#6b7280" }}>Subscription Fee ({confirmSub.feeRate}%)</span>
										<span style={{ color: "#92400e" }}>{formatSAR(confirmSub.feeBase)}</span>
									</div>
									<div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 10 }}>
										<span style={{ color: "#6b7280" }}>VAT ({feeSettings.vatRate || 15}%)</span>
										<span style={{ color: "#92400e" }}>{formatSAR(confirmSub.feeVat)}</span>
									</div>
								</>
							) : null}
							<div style={{ display: "flex", justifyContent: "space-between", fontSize: 15, paddingTop: 10, borderTop: "2px solid #e5e7eb" }}>
								<span style={{ fontWeight: 700, color: DARK }}>Total Deduction</span>
								<span style={{ fontWeight: 700, color: DARK, fontSize: 16 }}>{formatSAR(confirmSub.grandTotal)}</span>
							</div>
						</div>
						{/* Action buttons */}
						<div style={{ display: "flex", gap: 12 }}>
							<button onClick={() => setConfirmSub(null)}
								style={{ flex: 1, padding: "13px", background: "#f3f4f6", color: "#374151", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
								Cancel
							</button>
							<button
								onClick={async () => { const d = confirmSub.details; setConfirmSub(null); await executeTransaction(d); }}
								style={{ flex: 2, padding: "13px", background: `linear-gradient(135deg, ${DARK}, #1a5c3a)`, color: GOLD, border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: "pointer", letterSpacing: "0.3px" }}>
								Confirm Subscription
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Buy Modal */}
			{buyOpen && (
				<div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
					<div style={{ background: "#fff", borderRadius: 16, padding: "28px 32px", width: "100%", maxWidth: 420, boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
							<div>
								<h3 style={{ fontSize: 17, fontWeight: 700, color: "#111827", margin: 0 }}>Subscribe to Units</h3>
								<p style={{ fontSize: 13, color: "#6b7280", margin: "4px 0 0" }}>Enter number of units to purchase</p>
							</div>
							<button onClick={handleClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: 22 }}>×</button>
						</div>
						<form onSubmit={transaction.bind(this)}>
							<input type="hidden" name="action" value="buy" />
							<input type="hidden" name="propertyId" value={propid} />
							<div style={{ marginBottom: 16 }}>
								<label style={{ fontSize: 12.5, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 }}>Number of Units</label>
								<input type="number" name="units" min="1" max={availabletoken} onChange={changeData} value={changedata.units}
									style={{ width: "100%", padding: "12px 14px", border: "1.5px solid #e5e7eb", borderRadius: 9, fontSize: 14, outline: "none", boxSizing: "border-box" }}
									placeholder="Enter units" required />
								<p style={{ fontSize: 12, color: "#9ca3af", margin: "5px 0 0" }}>Available: {availabletoken} units</p>
							</div>
							<div style={{ marginBottom: 20 }}>
								<label style={{ fontSize: 12.5, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 }}>Total Amount (SAR)</label>
								<input type="number" name="amount" max={availablebalance} value={changedata.amount} readOnly
									style={{ width: "100%", padding: "12px 14px", border: "1.5px solid #e5e7eb", borderRadius: 9, fontSize: 14, background: "#f9fafb", boxSizing: "border-box" }} />
												<p style={{ fontSize: 12, color: "#9ca3af", margin: "5px 0 0" }}>Your balance: {formatSAR(availablebalance)}</p>
				</div>
				{changedata.units > 0 && feeSettings.subscriptionFee > 0 && (
					<div style={{ background: "#fffbeb", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#92400e", border: "1px solid #fde68a", marginBottom: 14 }}>
						<div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
							<span>Units cost:</span>
							<span style={{ fontWeight: 700 }}>{formatSAR(changedata.amount)}</span>
						</div>
						<div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
							<span>Subscription fee ({feeSettings.subscriptionFee}%):</span>
							<span style={{ fontWeight: 700 }}>{formatSAR(Math.round(changedata.amount * (feeSettings.subscriptionFee / 100) * 100) / 100)}</span>
						</div>
						<div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid #fde68a", paddingTop: 4, fontWeight: 700 }}>
							<span>Total deduction:</span>
							<span>{formatSAR(Math.round(changedata.amount * (1 + feeSettings.subscriptionFee / 100) * 100) / 100)}</span>
						</div>
					</div>
				)}
			<button type="submit" style={{ width: "100%", padding: "13px", background: "#c9a84c", color: "#fff", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
							Confirm Subscription
						</button>
						</form>
					</div>
				</div>
			)}

			{/* Sell Modal */}
			{sellOpen && (
				<div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
					<div style={{ background: "#fff", borderRadius: 16, padding: "28px 32px", width: "100%", maxWidth: 460, boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}>
						{/* Header */}
						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
							<div>
								<h3 style={{ fontSize: 17, fontWeight: 700, color: "#111827", margin: 0 }}>Sell Units</h3>
								<p style={{ fontSize: 13, color: "#6b7280", margin: "4px 0 0" }}>List your units on the secondary market</p>
							</div>
							<button onClick={handleClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: 22 }}>×</button>
						</div>
			{/* Market Price Info */}
				<div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "12px 16px", marginBottom: 18 }}>
					<div style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Market Reference</div>
					<div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
						<div style={{ flex: 1 }}>
							<div style={{ fontSize: 11, color: "#6b7280", marginBottom: 2 }}>Best Ask (Exchange)</div>
							<div style={{ fontSize: 15, fontWeight: 700, color: askPrice ? "#dc2626" : "#9ca3af" }}>
								{askPrice ? formatSAR(askPrice) : "No asks"}
							</div>
						</div>
						<div style={{ flex: 1 }}>
							<div style={{ fontSize: 11, color: "#6b7280", marginBottom: 2 }}>Best Bid (Exchange)</div>
							<div style={{ fontSize: 15, fontWeight: 700, color: bidPrice ? "#16a34a" : "#9ca3af" }}>
								{bidPrice ? formatSAR(bidPrice) : "No bids"}
							</div>
						</div>
						<div style={{ flex: 1 }}>
							<div style={{ fontSize: 11, color: "#6b7280", marginBottom: 2 }}>Last Traded</div>
							<div style={{ fontSize: 15, fontWeight: 700, color: lastTradedPrice && lastTradedPrice > tokenPrice ? "#16a34a" : lastTradedPrice && lastTradedPrice < tokenPrice ? "#dc2626" : DARK }}>
								{lastTradedPrice ? formatSAR(lastTradedPrice) : "—"}
							</div>
						</div>
					</div>
					<div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
						<div style={{ fontSize: 11, color: "#6b7280" }}>Issue Price: <strong style={{ color: DARK }}>{formatSAR(tokenPrice)}</strong></div>
						<div style={{ fontSize: 11, color: "#6b7280" }}>Your Holdings: <strong style={{ color: "#111827" }}>{userTokenBalance.toLocaleString()} units</strong></div>
					</div>
				</div>
						<form onSubmit={transaction.bind(this)}>
							<input type="hidden" name="action" value="sell" />
							<input type="hidden" name="propertyId" value={propid} />
							<input type="hidden" name="amount" value="0" />
							{/* Units field */}
							<div style={{ marginBottom: 16 }}>
								<label style={{ fontSize: 12.5, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 }}>Number of Units to Sell</label>
								<input type="number" name="units" min="1" max={userTokenBalance || 1}
									value={sellUnits}
									onChange={e => setSellUnits(e.target.value)}
									style={{ width: "100%", padding: "12px 14px", border: "1.5px solid #e5e7eb", borderRadius: 9, fontSize: 14, outline: "none", boxSizing: "border-box" }}
									placeholder="Enter units" required />
								<p style={{ fontSize: 12, color: "#9ca3af", margin: "5px 0 0" }}>You hold: {userTokenBalance.toLocaleString()} units</p>
							</div>
							{/* Suggested price field */}
							<div style={{ marginBottom: 16 }}>
								<label style={{ fontSize: 12.5, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 }}>Your Asking Price (SAR per unit)</label>
								<input type="number" name="sellPrice" min="1" step="0.01"
									value={sellPrice}
									onChange={e => setSellPrice(e.target.value)}
									style={{ width: "100%", padding: "12px 14px", border: "1.5px solid #e5e7eb", borderRadius: 9, fontSize: 14, outline: "none", boxSizing: "border-box" }}
									placeholder={`e.g. ${tokenPrice}`} required />
								<p style={{ fontSize: 12, color: "#9ca3af", margin: "5px 0 0" }}>
					{askPrice ? <>Best ask on exchange: <strong style={{ color: "#dc2626" }}>{formatSAR(askPrice)}</strong></> : <>Issue price: <strong>{formatSAR(tokenPrice)}</strong></>}
				</p>
							</div>
							{/* Total proceeds preview */}
							{sellUnits > 0 && sellPrice > 0 && (
								<div style={{ background: "#f0fdf4", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#15803d", border: "1px solid #bbf7d0", marginBottom: 14 }}>
									<div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
										<span>Gross proceeds:</span>
										<span style={{ fontWeight: 700 }}>{formatSAR(parseInt(sellUnits) * parseFloat(sellPrice))}</span>
									</div>
									{feeSettings.exchangeFeePerParty > 0 && (
										<div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
											<span>Exchange fee ({feeSettings.exchangeFeePerParty}%):</span>
											<span style={{ fontWeight: 700, color: "#dc2626" }}>-{formatSAR(parseInt(sellUnits) * parseFloat(sellPrice) * (feeSettings.exchangeFeePerParty / 100))}</span>
										</div>
									)}
									<div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid #bbf7d0", paddingTop: 4, fontWeight: 700 }}>
										<span>Net proceeds:</span>
										<span>{formatSAR(parseInt(sellUnits) * parseFloat(sellPrice) * (1 - feeSettings.exchangeFeePerParty / 100))}</span>
									</div>
								</div>
							)}
							{userTokenBalance === 0 && (
								<div style={{ background: "#fef2f2", borderRadius: 8, padding: "10px 14px", fontSize: 12.5, color: "#dc2626", border: "1px solid #fecaca", marginBottom: 14 }}>
									⚠ You don't hold any units of this property.
								</div>
							)}
				<button type="submit" disabled={userTokenBalance === 0} style={{ width: "100%", padding: "13px", background: userTokenBalance === 0 ? "#d1d5db" : "#c9a84c", color: "#fff", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: userTokenBalance === 0 ? "not-allowed" : "pointer" }}>
					Confirm Sale
				</button>
			</form>
			{/* Outstanding Orders */}
			{myOpenOrders.length > 0 && (
				<div style={{ marginTop: 20, borderTop: "1px solid #e5e7eb", paddingTop: 16 }}>
					<div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.04em" }}>Your Outstanding Sell Orders</div>
					<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
						{myOpenOrders.map((order, idx) => (
							<div key={idx} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#fef9ec", border: "1px solid #fde68a", borderRadius: 8, padding: "9px 14px" }}>
								<div>
									<span style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>{order.units.toLocaleString()} units</span>
									<span style={{ fontSize: 11, color: "#6b7280", marginLeft: 8 }}>@ {formatSAR(order.price)} / unit</span>
								</div>
								<div style={{ textAlign: "right" }}>
									<div style={{ fontSize: 13, fontWeight: 700, color: GOLD }}>{formatSAR(order.units * order.price)}</div>
									<div style={{ fontSize: 10, color: "#f59e0b", marginTop: 2 }}>● Open</div>
								</div>
							</div>
						))}
					</div>
				</div>
			)}
			</div>
			</div>
			)}
		</div>
	);
};

export default ViewProperty;
